<script lang="ts">
export type SegmentItemForm = {
  id: string
  itemType: SegmentItemType
  itemValue: string
  itemOption: string
  itemNot: boolean
}

export type SegmentItemType =
  | 'line-activation-login-link'
  | 'inflow-source'
  | 'click-url'
  | 'planner-enquete-answered'
  | 'room-status'
  | 'user-variable-definition'

export default {}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconClose from '@/components/icons/IconClose.vue'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import type { Option } from '@/types/app'
import type { ChatRoomStatus } from '@/types/schema.generated'

const model = defineModel<SegmentItemForm>({ required: true })

const emit = defineEmits<{
  (event: 'delete'): void
}>()

const props = withDefaults(
  defineProps<{
    segmentTypeOptions: { text: string; value: SegmentItemType }[]
    inflowSourceOptions: { text: string; value: string }[]
    clickUrlOptions: { text: string; value: string }[]
    plannerEnqueteOptions: { text: string; value: string }[]
    roomStatusOptions: { text: string; value: ChatRoomStatus }[]
    userVariableDefinitionOptions: {
      text: string
      value: string
      options: { text: string; value: string }[]
    }[]
  }>(),
  {
    inflowSourceOptions: () => [],
    clickUrlOptions: () => [],
    plannerEnqueteOptions: () => [],
    roomStatusOptions: () => [],
    userVariableDefinitionOptions: () => [],
  }
)

const segmentItemType = computed({
  get(): SegmentItemType {
    return model.value.itemType
  },
  set(value: SegmentItemType): void {
    model.value = {
      ...model.value,
      itemType: value,
      itemValue: '',
      itemNot: false,
      itemOption: '',
    }
  },
})

const segmentValue = computed({
  get(): string {
    if (segmentValueDisabled.value) {
      return ''
    }
    return model.value.itemValue
  },
  set(value: string): void {
    model.value = { ...model.value, itemValue: value, itemOption: '' }
  },
})

const segmentOption = computed({
  get(): string {
    if (model.value.itemOption.length === 0) {
      return ''
    }
    return model.value.itemOption
  },
  set(value: string): void {
    model.value = { ...model.value, itemOption: value }
  },
})

const segmentNot = computed({
  get(): boolean {
    return model.value.itemNot
  },
  set(value: boolean): void {
    model.value = { ...model.value, itemNot: value }
  },
})

const segmentValueDisabled = computed(() => {
  return ['line-activation-login-link', undefined].includes(
    segmentItemType.value
  )
})

const segmentValueOptions = computed(() => {
  switch (segmentItemType.value) {
    case 'inflow-source':
      return props.inflowSourceOptions
    case 'click-url':
      return props.clickUrlOptions
    case 'planner-enquete-answered':
      return props.plannerEnqueteOptions
    case 'room-status':
      return props.roomStatusOptions
    case 'user-variable-definition':
      return props.userVariableDefinitionOptions
    default:
      return []
  }
})

const segmentOptionDisabled = computed(() => {
  return segmentValue.value === ''
})

const segmentOptionOptions = computed(() => {
  if (segmentItemType.value === 'user-variable-definition') {
    if (segmentValue.value === '') {
      return []
    }
    const userVariableDefinitionOption =
      props.userVariableDefinitionOptions.find(
        o => o.value === segmentValue.value
      )
    if (userVariableDefinitionOption === undefined) {
      throw new Error('選択肢が設定されていません')
    }
    return userVariableDefinitionOption.options
  }
  return []
})

// Linter が型まわりを組んでくれていないので disable
// 型の上で必ず return していることが分かる
// eslint-disable-next-line vue/return-in-computed-property
const notOptions = computed<Array<Option<boolean>>>(() => {
  switch (segmentItemType.value) {
    case 'inflow-source':
    case 'click-url':
    case 'room-status':
      return [
        {
          text: '一致する',
          value: false,
        },
        {
          text: '一致しない',
          value: true,
        },
      ]
    case 'line-activation-login-link':
      return [
        {
          text: '連携済み',
          value: false,
        },
        {
          text: '未連携',
          value: true,
        },
      ]
    case 'planner-enquete-answered':
      return [
        {
          text: '回答済み',
          value: false,
        },
        {
          text: '未回答',
          value: true,
        },
      ]
    case 'user-variable-definition':
      return [
        {
          text: '選択している',
          value: false,
        },
        {
          text: '選択していない',
          value: true,
        },
      ]
    default:
      assertExhaustive(segmentItemType.value)
  }
})

const deleteButtonClicked = (): void => {
  emit('delete')
}
const isUserVariableDefinition = computed(
  () => segmentItemType.value === 'user-variable-definition'
)
</script>

<template>
  <div
    :class="
      isUserVariableDefinition ? $style.formUserVariableDefinition : $style.form
    "
  >
    <DolphinSelector
      v-model="segmentItemType"
      :options="segmentTypeOptions"
      :ellipsis="true"
    />
    <DolphinSelector
      :class="$style.form__value"
      v-model="segmentValue"
      :options="segmentValueOptions"
      :disabled="segmentValueDisabled"
      :ellipsis="true"
    />
    <DolphinSelector
      v-if="isUserVariableDefinition"
      :class="$style.form__value"
      v-model="segmentOption"
      :options="segmentOptionOptions"
      :disabled="segmentOptionDisabled"
      :ellipsis="true"
    />
    <DolphinSelector
      v-model="segmentNot"
      :options="notOptions"
      :ellipsis="true"
    />
    <FlatButton size="small" variant="plain">
      <div :class="$style.deleteButton" @click="deleteButtonClicked">
        <IconClose /> 削除
      </div>
    </FlatButton>
  </div>
</template>

<style module lang="scss">
.form {
  display: grid;
  grid-template-columns: 200px 1fr 160px 80px;
  grid-column-gap: 16px;
  justify-content: space-between;
  align-items: center;

  &__value {
    overflow: hidden;
  }
}

.formUserVariableDefinition {
  display: grid;
  grid-template-columns: 200px 1fr 1fr 160px 80px;
  grid-column-gap: 16px;
  justify-content: space-between;
  align-items: center;

  &__value {
    overflow: hidden;
  }
}

.deleteButton {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
